import React from 'react';
import { Calendar, Clock, Award, Edit3, Trash2 } from 'lucide-react';
import { Activity } from '../../types';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';

interface ActivityCardProps {
  activity: Activity;
  onEdit?: (activity: Activity) => void;
  onDelete?: (id: string) => void;
}

export const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  onEdit,
  onDelete,
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'accent' | 'success' | 'warning'> = {
      sports: 'primary',
      arts: 'accent',
      volunteer: 'success',
      academic: 'secondary',
      leadership: 'warning',
      internship: 'primary',
      work: 'secondary',
      other: 'accent',
    };
    return colors[category] || 'secondary';
  };

  return (
    <Card hover className="group">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {activity.title}
          </h3>
          <Badge variant={getCategoryColor(activity.category)} size="sm">
            {activity.category.replace('-', ' ')}
          </Badge>
        </div>

        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <button
              onClick={() => onEdit(activity)}
              className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
            >
              <Edit3 size={16} />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(activity.id)}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      <p className="text-gray-600 mb-4 line-clamp-2">{activity.description}</p>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center text-gray-500">
          <Calendar size={14} className="mr-2" />
          <span>
            {formatDate(activity.startDate)} - {
              activity.ongoing ? 'Present' : formatDate(activity.endDate!)
            }
          </span>
        </div>

        <div className="flex items-center text-gray-500">
          <Clock size={14} className="mr-2" />
          <span>{activity.hoursPerWeek} hrs/week</span>
        </div>
      </div>

      {activity.leadershipRole && (
        <div className="flex items-center text-sm text-primary-600 mb-3">
          <Award size={14} className="mr-2" />
          <span className="font-medium">{activity.leadershipRole}</span>
        </div>
      )}

      {activity.achievements.length > 0 && (
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs font-medium text-gray-500 mb-2">Key Achievements:</p>
          <ul className="text-sm text-gray-600 space-y-1">
            {activity.achievements.slice(0, 2).map((achievement, index) => (
              <li key={index} className="flex items-start">
                <span className="w-1 h-1 bg-primary-400 rounded-full mt-2 mr-2 flex-shrink-0" />
                {achievement}
              </li>
            ))}
            {activity.achievements.length > 2 && (
              <li className="text-primary-600 text-xs">
                +{activity.achievements.length - 2} more achievements
              </li>
            )}
          </ul>
        </div>
      )}
    </Card>
  );
};